import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { 
  MessageCircle, X, Send, Bot, User, Loader2,
  Package, ShoppingCart, ArrowRight, Sparkles
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function GlobalChatbot() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState(null); // 'exporter' | 'buyer' | null
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Check if user is logged in
  const isLoggedIn = !!localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Check if on admin pages (used in JSX, not early return)
  const isAdminPage = location.pathname.startsWith('/admin');

  // Initial welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = isLoggedIn
        ? {
            role: 'assistant',
            content: `Hi ${user.full_name?.split(' ')[0] || 'there'}! 👋 I'm GlobalVyapar AI. How can I help you today?\n\nI can help you with:\n• Finding products or suppliers\n• Understanding platform features\n• Managing your listings\n• Export/import queries`,
            timestamp: new Date()
          }
        : {
            role: 'assistant',
            content: `Welcome to GlobalVyapar! 👋 I'm here to help you get started.\n\nAre you an **exporter looking for buyers**, or a **buyer looking for Indian suppliers**?`,
            timestamp: new Date(),
            showOptions: true
          };
      
      setMessages([welcomeMessage]);
    }
  }, [isOpen]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleUserTypeSelect = (type) => {
    setUserType(type);
    
    const userMessage = {
      role: 'user',
      content: type === 'exporter' ? "I'm an exporter looking for buyers" : "I'm a buyer looking for Indian suppliers",
      timestamp: new Date()
    };

    const responseMessage = type === 'exporter'
      ? {
          role: 'assistant',
          content: `Great! GlobalVyapar is perfect for Indian exporters like you! 🚀\n\nHere's what you'll get:\n\n✅ **AI Auto-Reply** - Respond to buyers instantly\n✅ **AI Negotiation** - Never drop below your floor price\n✅ **Verified Buyers** - Only deal with legitimate businesses\n✅ **30-Day Follow-ups** - Never lose a lead\n\nReady to start exporting smarter?`,
          timestamp: new Date(),
          showSignup: 'seller'
        }
      : {
          role: 'assistant',
          content: `Welcome! You'll find thousands of verified Indian suppliers on GlobalVyapar! 🌏\n\nBenefits for buyers:\n\n✅ **Verified Sellers** - GSTIN & IEC checked\n✅ **Direct Communication** - No middlemen\n✅ **Quality Products** - Spices, textiles, grains & more\n✅ **Competitive Pricing** - Direct from source\n\nCreate a free account to start browsing!`,
          timestamp: new Date(),
          showSignup: 'buyer'
        };

    setMessages(prev => [...prev, userMessage, responseMessage]);
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/api/ai/smart-chat`, {
        message: input.trim(),
        session_id: sessionId,
        context: {
          is_logged_in: isLoggedIn,
          user_type: userType || user.role,
          page: location.pathname
        }
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response || "I'm sorry, I couldn't process that. Please try again.",
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSignupClick = (role) => {
    setIsOpen(false);
    navigate(`/auth?tab=register&role=${role}`);
  };

  // Don't render on admin pages
  if (isAdminPage) {
    return null;
  }

  return (
    <>
      {/* Chat Button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 rounded-full w-14 h-14 shadow-lg transition-all duration-300 ${
          isOpen 
            ? 'bg-slate-700 hover:bg-slate-800 rotate-90' 
            : 'bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700'
        }`}
        data-testid="global-chatbot-button"
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <MessageCircle className="h-6 w-6" />
        )}
      </Button>

      {/* Chat Window */}
      {isOpen && (
        <Card className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-3rem)] h-[500px] max-h-[70vh] flex flex-col shadow-2xl border-0 overflow-hidden animate-in slide-in-from-bottom-5 duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Sparkles className="h-4 w-4" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">GlobalVyapar</h3>
                <p className="text-xs text-teal-100">AI Assistant</p>
              </div>
            </div>
            <Badge variant="secondary" className="bg-white/20 text-white text-xs">
              Online
            </Badge>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            <div className="space-y-4">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] ${msg.role === 'user' ? 'order-2' : ''}`}>
                    {msg.role === 'assistant' && (
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center">
                          <Bot className="h-3 w-3 text-teal-600" />
                        </div>
                        <span className="text-xs text-slate-500">GlobalVyapar AI</span>
                      </div>
                    )}
                    
                    <div className={`rounded-2xl px-4 py-2.5 ${
                      msg.role === 'user' 
                        ? 'bg-teal-600 text-white rounded-br-md' 
                        : 'bg-slate-100 text-slate-800 rounded-bl-md'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>

                    {/* User Type Options */}
                    {msg.showOptions && !userType && (
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 h-auto py-3 flex-col gap-1 border-teal-200 hover:bg-teal-50 hover:border-teal-400"
                          onClick={() => handleUserTypeSelect('exporter')}
                        >
                          <Package className="h-5 w-5 text-teal-600" />
                          <span className="text-xs">I'm an Exporter</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 h-auto py-3 flex-col gap-1 border-blue-200 hover:bg-blue-50 hover:border-blue-400"
                          onClick={() => handleUserTypeSelect('buyer')}
                        >
                          <ShoppingCart className="h-5 w-5 text-blue-600" />
                          <span className="text-xs">I'm a Buyer</span>
                        </Button>
                      </div>
                    )}

                    {/* Signup CTA */}
                    {msg.showSignup && (
                      <Button
                        className="w-full mt-3 bg-teal-600 hover:bg-teal-700"
                        onClick={() => handleSignupClick(msg.showSignup)}
                      >
                        Create Free Account <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 rounded-2xl px-4 py-3 rounded-bl-md">
                    <Loader2 className="h-5 w-5 animate-spin text-teal-600" />
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="p-3 border-t bg-white">
            <form 
              onSubmit={(e) => { e.preventDefault(); handleSend(); }}
              className="flex gap-2"
            >
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                className="flex-1"
                disabled={loading}
              />
              <Button 
                type="submit" 
                size="icon"
                disabled={!input.trim() || loading}
                className="bg-teal-600 hover:bg-teal-700"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </Card>
      )}
    </>
  );
}
